import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import PublicHeader from "@/components/public/PublicHeader";
import PublicFooter from "@/components/public/PublicFooter";
import ProcessDiagram from "@/components/public/ProcessDiagram";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { trackLead } from "@/lib/pixel";

const FIGURES = [
  { n: "6+", label: "Años acompañando dueños y compradores" },
  { n: "[04]", label: "Etapas de certificación por unidad" },
  { n: "4+", label: "Bancos y financieras aliadas" },
  { n: "100%", label: "Certificado antes de exhibirse" },
];

function Hero() {
  return (
    <section className="relative overflow-hidden bg-white pt-28 pb-20 md:pt-40 md:pb-32" data-testid="section-hero">
      <div className="container-x grid items-center gap-12 md:grid-cols-2 md:gap-14">
        <div className="flex flex-col justify-center">
          <p className="mono text-xs uppercase tracking-[0.2em] text-[#A0553F]">
            La Dupla Automotriz · Certificación vehicular
          </p>
          <h1 className="mt-5 font-serif text-4xl leading-[1.02] text-[#3E5641] md:text-6xl lg:text-[68px]">
            Autos <span className="italic text-[#A0553F]">certificados</span>.
          </h1>
          <p className="mt-6 max-w-xl font-serif text-xl leading-snug text-[#3E5641] md:text-2xl">
            Autos usados, tratados como activos que se certifican, no como inventario sin revisar.
          </p>
          <p className="mt-5 max-w-xl text-base text-[#6B7678] md:text-lg">
            La Dupla Automotriz es un sistema de gestión y certificación: cada auto pasa por cuatro etapas antes de que un comprador lo vea. Aquí no vas a encontrar catálogo abierto — vas a encontrar un proceso.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#vender" className="btn-outline-dark" data-testid="hero-sell-btn">
              Quiero vender mi auto
              <ArrowUpRight size={16} />
            </a>
            <a href="#comprar" className="btn-primary" data-testid="hero-buy-btn">
              Quiero comprar un auto
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
        <div className="relative">
          <div
            className="mx-auto aspect-[2/3] w-full max-w-[380px] overflow-hidden rounded-sm border border-[#3E5641]/10 bg-[#F1F3F3]"
            data-testid="hero-image-frame"
          >
            <img
              src="/ladupla-hero.png"
              alt="Equipo La Dupla Automotriz recibiendo a un cliente frente al establecimiento"
              className="h-full w-full object-cover object-center"
              loading="eager"
              data-testid="hero-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function SellSection() {
  return (
    <section id="vender" className="bg-[#F1F3F3] py-24 md:py-32" data-testid="section-vender">
      <div className="container-x grid gap-12 md:grid-cols-12">
        <div className="md:col-span-6">
          <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Para dueños</p>
          <h2 className="mt-3 font-serif text-4xl text-[#3E5641] md:text-5xl">
            Vende con acompañamiento, no con prisa.
          </h2>
          <p className="mt-5 max-w-lg text-[#6B7678]">
            Registras tu auto con datos básicos y contacto. Nosotros hacemos el resto en persona: cotejo documental, revisión y valuación. No pedimos documentos legales por internet — nunca. Las fotos las pedimos después por WhatsApp.
          </p>
          <Link to="/vender" className="btn-outline-dark mt-8" data-testid="section-vender-cta">
            Registrar mi auto
            <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="md:col-span-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="card-editorial">
              <p className="mono text-xs text-[#A0553F]">01</p>
              <p className="mt-2 text-sm text-[#3E5641]">Registro con datos básicos y fotos</p>
            </div>
            <div className="card-editorial">
              <p className="mono text-xs text-[#A0553F]">02</p>
              <p className="mt-2 text-sm text-[#3E5641]">Cotejo documental presencial</p>
            </div>
            <div className="card-editorial">
              <p className="mono text-xs text-[#A0553F]">03</p>
              <p className="mt-2 text-sm text-[#3E5641]">Valuación con rango sugerido</p>
            </div>
            <div className="card-editorial">
              <p className="mono text-xs text-[#A0553F]">04</p>
              <p className="mt-2 text-sm text-[#3E5641]">Consignación y exhibición</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BuySection() {
  return (
    <section id="comprar" className="bg-[#3E5641] py-24 text-white md:py-32" data-testid="section-comprar">
      <div className="container-x grid gap-12 md:grid-cols-12">
        <div className="md:col-span-6">
          <p className="mono text-xs uppercase tracking-widest text-[#D6866B]">Para compradores</p>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl">
            El crédito va primero. El auto, después.
          </h2>
          <p className="mt-5 max-w-lg text-white/70">
            No mostramos catálogo abierto en esta página. Antes de mirar autos, construimos contigo un expediente y activamos tu crédito con nuestros bancos aliados. Así cuando eliges, no hay sorpresas.
          </p>
          <Link to="/comprar" className="btn-primary mt-8" data-testid="section-comprar-cta">
            Empezar mi crédito
            <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="md:col-span-6">
          <div className="border border-white/15 p-8">
            <p className="mono text-xs uppercase tracking-widest text-[#D6866B]">¿Por qué así?</p>
            <ul className="mt-4 space-y-3 text-sm text-white/80">
              <li className="flex gap-3"><span className="mono text-[#D6866B]">→</span> Evitas enamorarte de un auto que tu crédito no alcanza.</li>
              <li className="flex gap-3"><span className="mono text-[#D6866B]">→</span> Sabes tu tasa y plazo antes de decidir.</li>
              <li className="flex gap-3"><span className="mono text-[#D6866B]">→</span> Puedes cerrar la compra con o sin unidad de Dupla Automotriz.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function FiguresSection() {
  return (
    <section className="border-y border-[#3E5641]/10 bg-white py-20" data-testid="section-figures">
      <div className="container-x grid gap-10 md:grid-cols-4">
        {FIGURES.map((f) => (
          <div key={f.label}>
            <p className="mono text-4xl leading-none text-[#3E5641] md:text-5xl">{f.n}</p>
            <p className="mt-3 max-w-[220px] text-sm text-[#6B7678]">{f.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const CAT_ORDER = [
  "Financieras y bancos",
  "Mecánicos",
  "Hojalatería y pintura",
  "Agencias de autos nuevos",
  "Otro",
];

function PartnersSection() {
  const [partners, setPartners] = useState([]);
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get("/partners/public").then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  return (
    <section id="socios" className="bg-[#F1F3F3] py-24 md:py-28" data-testid="section-partners">
      <div className="container-x">
        <div className="mb-10 flex items-end justify-between gap-6 md:mb-14">
          <div className="max-w-2xl">
            <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Socios comerciales</p>
            <h2 className="mt-3 font-serif text-4xl text-[#3E5641] md:text-5xl">
              No trabajamos solos.
            </h2>
            <p className="mt-4 max-w-xl text-sm text-[#6B7678]">
              Bancos, financieras, talleres y agencias con quienes ya operamos como aliados formales.
            </p>
          </div>
          {partners.length > 0 && (
            <div className="hidden shrink-0 gap-2 md:flex">
              <button
                onClick={() => scrollBy(-1)}
                className="flex h-11 w-11 items-center justify-center border border-[#3E5641]/20 bg-white text-[#3E5641] transition-colors hover:bg-[#3E5641] hover:text-white"
                aria-label="Anterior"
                data-testid="partners-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => scrollBy(1)}
                className="flex h-11 w-11 items-center justify-center border border-[#3E5641]/20 bg-white text-[#3E5641] transition-colors hover:bg-[#3E5641] hover:text-white"
                aria-label="Siguiente"
                data-testid="partners-next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        {partners.length === 0 ? (
          <p className="text-sm text-[#6B7678]" data-testid="partners-empty">
            Los socios comerciales aparecerán aquí una vez que estén cargados desde el panel de administración.
          </p>
        ) : (
          <div
            className="partners-carousel relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="partners-carousel"
          >
            <div
              ref={trackRef}
              className={`partners-track flex gap-6 overflow-x-auto scroll-smooth pb-4 ${paused ? "is-paused" : ""}`}
              style={{ scrollbarWidth: "none" }}
            >
              {partners.map((p) => <PartnerCard key={p.id} partner={p} />)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function PartnerCard({ partner: p }) {
  const isBank = p.categoria === "Financieras y bancos";
  const inner = (
    <>
      <div className="flex h-32 items-center justify-center">
        {p.logo_path ? (
          <img
            src={fileUrl(p.logo_path)}
            alt={p.nombre}
            className="max-h-28 max-w-full object-contain"
            loading="lazy"
          />
        ) : (
          <span className="font-serif text-2xl text-[#3E5641]">{p.nombre}</span>
        )}
      </div>
      <div className="mt-6 border-t border-[#3E5641]/10 pt-4">
        <p className="mono text-[10px] uppercase tracking-widest text-[#A0553F]">{p.categoria}</p>
        <p className="mt-1 font-serif text-lg text-[#3E5641]">{p.nombre}</p>
        {p.descripcion && (
          <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-[#6B7678]" data-testid={`partner-desc-${p.id}`}>
            {p.descripcion}
          </p>
        )}
        {isBank && (
          <p className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-[#C4674F] group-hover:text-[#A0553F]">
            Empezar mi crédito con {p.nombre} <ArrowUpRight size={12} />
          </p>
        )}
      </div>
    </>
  );

  const baseClass =
    "group relative flex w-[300px] shrink-0 flex-col justify-between border border-[#3E5641]/10 bg-white p-6 transition-colors";

  if (isBank) {
    return (
      <Link
        to="/comprar"
        className={`${baseClass} hover:border-[#C4674F] hover:shadow-sm cursor-pointer`}
        data-testid={`partner-card-${p.id}`}
        data-partner-category={p.categoria}
        title={`Empezar crédito con ${p.nombre}`}
      >
        {inner}
      </Link>
    );
  }

  return (
    <article
      className={`${baseClass} hover:border-[#C4674F]`}
      data-testid={`partner-card-${p.id}`}
      data-partner-category={p.categoria}
      title={p.nombre}
    >
      {inner}
    </article>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-white text-[#3E5641]" data-testid="home">
      <PublicHeader />
      <main>
        <Hero />
        <ProcessDiagram />
        <SellSection />
        <BuySection />
        {/* <BusinessPartnerSection /> temporarily hidden from the public home per
            product decision. The section component + its Dialog + the admin
            modules `Solicitudes de socios` and `Socios comerciales` remain
            intact and can be re-enabled by uncommenting this line. */}
        <AffiliateProgramSection />
        <FiguresSection />
        <PartnersSection />
      </main>
      <PublicFooter />
    </div>
  );
}

const CAT_OPTIONS = ["Financieras y bancos", "Mecánicos", "Hojalatería y pintura", "Agencias de autos nuevos", "Otro"];

function BusinessPartnerSection() {
  const [open, setOpen] = useState(false);
  return (
    <section id="socios-solicitud" className="bg-white py-24 md:py-28" data-testid="section-partner-application">
      <div className="container-x grid gap-12 md:grid-cols-12 md:items-center">
        <div className="md:col-span-6">
          <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Para negocios</p>
          <h2 className="mt-3 font-serif text-4xl text-[#3E5641] md:text-5xl">
            Quiero ser socio comercial.
          </h2>
          <p className="mt-5 max-w-lg text-[#6B7678]">
            Bancos, financieras, talleres, hojalaterías y agencias de autos nuevos: si tu negocio quiere sumarse
            al ecosistema La Dupla Automotriz como aliado formal, aquí empieza la conversación. Solo aceptamos negocios que
            pasen nuestro filtro de calidad — no es un directorio abierto.
          </p>
          <button
            onClick={() => setOpen(true)}
            className="btn-outline-dark mt-8"
            data-testid="partner-app-open"
          >
            Postular mi negocio
            <ArrowUpRight size={16} />
          </button>
        </div>
        <div className="md:col-span-6">
          <div className="grid grid-cols-2 gap-3">
            {CAT_OPTIONS.slice(0, 4).map((c, i) => (
              <div key={c} className="card-editorial">
                <p className="mono text-xs text-[#A0553F]">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-2 text-sm text-[#3E5641]">{c}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {open && <BusinessPartnerDialog onClose={() => setOpen(false)} />}
    </section>
  );
}

function BusinessPartnerDialog({ onClose }) {
  const [form, setForm] = useState({
    nombre_negocio: "", categoria: "Financieras y bancos", nombre_contacto: "",
    telefono: "", correo: "", descripcion: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/partner-applications", {
        nombre_negocio: form.nombre_negocio.trim(),
        categoria: form.categoria,
        nombre_contacto: form.nombre_contacto.trim(),
        telefono: form.telefono.trim(),
        correo: form.correo.trim(),
        descripcion: form.descripcion.trim(),
      });
      setDone(true);
      trackLead({ content_name: "Quiero ser socio comercial", content_category: "partner" });
      toast.success("Postulación enviada. Revisaremos tu negocio.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="partner-app-dialog">
        {done ? (
          <div className="py-6 text-center">
            <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Postulación recibida</p>
            <h3 className="mt-3 font-serif text-2xl text-[#3E5641]">Gracias por proponer tu negocio.</h3>
            <p className="mt-3 text-sm text-[#6B7678]">
              Un asesor revisará tu perfil y te contactará. La aprobación como socio comercial es manual y no está garantizada.
            </p>
            <button onClick={onClose} className="btn-primary mt-6" data-testid="partner-app-close">Cerrar</button>
          </div>
        ) : (
          <form onSubmit={save}>
            <DialogHeader><DialogTitle className="font-serif text-2xl">Postular mi negocio</DialogTitle></DialogHeader>
            <div className="mt-4 space-y-4">
              <div>
                <Label className="field-label">Nombre del negocio *</Label>
                <Input required value={form.nombre_negocio} onChange={(e) => setF("nombre_negocio", e.target.value)} data-testid="partner-app-nombre-negocio" />
              </div>
              <div>
                <Label className="field-label">Categoría *</Label>
                <Select value={form.categoria} onValueChange={(v) => setF("categoria", v)}>
                  <SelectTrigger data-testid="partner-app-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CAT_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="field-label">Nombre de contacto *</Label>
                <Input required value={form.nombre_contacto} onChange={(e) => setF("nombre_contacto", e.target.value)} data-testid="partner-app-nombre-contacto" />
              </div>
              <div>
                <Label className="field-label">Teléfono *</Label>
                <Input required value={form.telefono} onChange={(e) => setF("telefono", e.target.value)} data-testid="partner-app-tel" />
              </div>
              <div>
                <Label className="field-label">Correo *</Label>
                <Input type="email" required value={form.correo} onChange={(e) => setF("correo", e.target.value)} data-testid="partner-app-correo" />
              </div>
              <div>
                <Label className="field-label">Descripción breve del negocio *</Label>
                <Textarea required rows={3} value={form.descripcion} onChange={(e) => setF("descripcion", e.target.value)} data-testid="partner-app-descripcion" />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <button type="button" onClick={onClose} className="btn-outline-dark">Cancelar</button>
              <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="partner-app-submit">
                {submitting ? "Enviando..." : "Enviar postulación"}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AffiliateProgramSection() {
  const [open, setOpen] = useState(false);
  return (
    <section id="afiliados" className="bg-[#F1F3F3] py-24 md:py-28" data-testid="section-affiliate-application">
      <div className="container-x grid gap-12 md:grid-cols-12 md:items-center">
        <div className="md:col-span-7">
          <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Para personas</p>
          <h2 className="mt-3 font-serif text-4xl text-[#3E5641] md:text-5xl">
            Vende con Dupla Automotriz.
          </h2>
          <p className="mt-5 max-w-xl text-[#6B7678]">
            Programa de afiliados individuales. Si te dedicas — o quieres dedicarte — a vender autos y servicios,
            te sumamos a La Dupla Automotriz para que generes ingresos por comisión sobre cada operación cerrada. No es un empleo,
            es una relación de colaboración por resultados.
          </p>
          <p className="mt-3 max-w-xl text-sm text-[#6B7678]">
            El seguimiento y la afiliación real se hacen por WhatsApp, uno a uno. Sin plataforma automática.
          </p>
          <button
            onClick={() => setOpen(true)}
            className="btn-primary mt-8"
            data-testid="affiliate-app-open"
          >
            Quiero afiliarme
            <ArrowUpRight size={16} />
          </button>
        </div>
        <div className="md:col-span-5">
          <div className="border border-[#3E5641]/10 bg-white p-8">
            <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">¿Cómo funciona?</p>
            <ul className="mt-4 space-y-3 text-sm text-[#3E5641]">
              <li className="flex gap-3"><span className="mono text-[#A0553F]">→</span> Envías tus datos.</li>
              <li className="flex gap-3"><span className="mono text-[#A0553F]">→</span> Te contactamos por WhatsApp.</li>
              <li className="flex gap-3"><span className="mono text-[#A0553F]">→</span> Definimos comisiones y condiciones.</li>
              <li className="flex gap-3"><span className="mono text-[#A0553F]">→</span> Empiezas a referir y a ganar.</li>
            </ul>
          </div>
        </div>
      </div>
      {open && <AffiliateProgramDialog onClose={() => setOpen(false)} />}
    </section>
  );
}

function AffiliateProgramDialog({ onClose }) {
  const [form, setForm] = useState({
    nombre: "", telefono: "", correo: "", ciudad: "", experiencia_ventas: "no",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/affiliate-applications", {
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim(),
        correo: form.correo.trim(),
        ciudad: form.ciudad.trim(),
        experiencia_ventas: form.experiencia_ventas === "si",
      });
      setDone(true);
      trackLead({ content_name: "Vende con Dupla Automotriz", content_category: "affiliate" });
      toast.success("Solicitud enviada. Te contactamos por WhatsApp.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="affiliate-app-dialog">
        {done ? (
          <div className="py-6 text-center">
            <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Solicitud recibida</p>
            <h3 className="mt-3 font-serif text-2xl text-[#3E5641]">Gracias por sumarte.</h3>
            <p className="mt-3 text-sm text-[#6B7678]">
              Nos comunicaremos contigo por WhatsApp para explicarte cómo trabajamos y afinar los detalles de comisión.
            </p>
            <button onClick={onClose} className="btn-primary mt-6" data-testid="affiliate-app-close">Cerrar</button>
          </div>
        ) : (
          <form onSubmit={save}>
            <DialogHeader><DialogTitle className="font-serif text-2xl">Quiero afiliarme</DialogTitle></DialogHeader>
            <div className="mt-4 space-y-4">
              <div>
                <Label className="field-label">Nombre completo *</Label>
                <Input required value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} data-testid="affiliate-app-nombre" />
              </div>
              <div>
                <Label className="field-label">Teléfono *</Label>
                <Input required value={form.telefono} onChange={(e) => setF("telefono", e.target.value)} data-testid="affiliate-app-tel" />
              </div>
              <div>
                <Label className="field-label">Correo *</Label>
                <Input type="email" required value={form.correo} onChange={(e) => setF("correo", e.target.value)} data-testid="affiliate-app-correo" />
              </div>
              <div>
                <Label className="field-label">Ciudad *</Label>
                <Input required value={form.ciudad} onChange={(e) => setF("ciudad", e.target.value)} data-testid="affiliate-app-ciudad" />
              </div>
              <div>
                <Label className="field-label">¿Tienes experiencia previa en ventas de autos? *</Label>
                <RadioGroup value={form.experiencia_ventas} onValueChange={(v) => setF("experiencia_ventas", v)} className="mt-2 flex gap-6">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-[#3E5641]">
                    <RadioGroupItem value="si" data-testid="affiliate-app-exp-si" /> Sí
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-[#3E5641]">
                    <RadioGroupItem value="no" data-testid="affiliate-app-exp-no" /> No
                  </label>
                </RadioGroup>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <button type="button" onClick={onClose} className="btn-outline-dark">Cancelar</button>
              <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="affiliate-app-submit">
                {submitting ? "Enviando..." : "Enviar solicitud"}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
